import React, { useEffect, useRef, lazy } from 'react'
import {
  motion,
  useScroll,
  useTransform,
  useAnimationControls,
  useInView,
} from 'framer-motion'
import {
  CodeIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ZapIcon,
  LockIcon,
  RefreshCwIcon,
  PlayIcon,
  TerminalIcon,
} from 'lucide-react'
import { isMobile } from '../utils/deviceDetection'
import { useInView } from '../utils/useInView'
export const PigeonAPI = () => {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
    layoutEffect: !isMobile(),
  })
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: false,
    threshold: 0.1,
  })
  // Combine refs
  const combinedRef = (el: HTMLElement | null) => {
    // @ts-ignore - simple ref merging
    ref.current = el
    // @ts-ignore - simple ref merging
    inViewRef.current = el
  }
  // Cinematic animations - simplified for mobile
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile() ? [0, 0] : [50, -50],
  ) // No parallax on mobile
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.6, 1], [0, 1, 1, 0])
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1])
  // Animation controls for flowing lines
  const lineControls = useAnimationControls()
  useEffect(() => {
    // Only animate when in view and not on mobile
    if (inView && !isMobile()) {
      const animateLines = async () => {
        await lineControls.start({
          pathLength: 1,
          transition: {
            duration: 1.5,
            ease: 'easeInOut',
          },
        })
      }
      animateLines()
    }
  }, [inView])
  // Define integration icons
  const integrationIcons = [
    {
      id: 'outlook',
      imageUrl: 'https://i.imgur.com/N0HAjSX.png',
      name: 'Outlook',
    },
    {
      id: 'gmail',
      imageUrl: 'https://i.imgur.com/sbOTl27.png',
      name: 'Gmail',
    },
    {
      id: 'imageright',
      imageUrl: 'https://i.imgur.com/dwL552b.png',
      name: 'ImageRight',
      isExtraLarge: true,
    },
    {
      id: 'vertafore',
      imageUrl: 'https://i.imgur.com/s8wSIqV.png',
      name: 'Vertafore',
      isExtraLarge: true,
    },
    {
      id: 'origami-risk',
      imageUrl: 'https://i.imgur.com/4pNSiT4.png',
      name: 'Origami Risk',
    },
    {
      id: 'dyad',
      imageUrl: 'https://i.imgur.com/mI7ssID.png',
      name: 'Dyad',
    },
    {
      id: 'guidewire',
      imageUrl: 'https://i.imgur.com/pZnMhBh.png',
      name: 'Guidewire',
      isLarger: true,
    },
    {
      id: 'duck-creek',
      imageUrl: 'https://i.imgur.com/UaSme4L.png',
      name: 'Duck Creek',
    },
    {
      id: 'snapsheet',
      imageUrl: 'https://i.imgur.com/z9OsFZw.png',
      name: 'Snapsheet',
    },
    {
      id: 'sharepoint',
      imageUrl: 'https://i.imgur.com/R2HP2ef.png',
      name: 'Sharepoint',
    },
    {
      id: 'bindhq',
      imageUrl: 'https://i.imgur.com/iTgz2Bi.png',
      name: 'BindHQ',
    },
    {
      id: 'applied-epic',
      imageUrl: 'https://i.imgur.com/CLv63Ar.png',
      name: 'Applied Epic',
    },
    {
      id: 'salesforce',
      imageUrl: 'https://i.imgur.com/ARGt4GI.png',
      name: 'Salesforce',
    },
    {
      id: 'hubspot',
      imageUrl: 'https://i.imgur.com/L4CTPky.png',
      name: 'HubSpot',
    },
    {
      id: 'zoho-crm',
      imageUrl: 'https://i.imgur.com/DkyqoiT.png',
      name: 'Zoho CRM',
    },
    {
      id: 'pipedrive',
      imageUrl: 'https://i.imgur.com/Bw4nlKD.png',
      name: 'Pipedrive',
    },
    {
      id: 'sharepoint-2',
      imageUrl: 'https://i.imgur.com/R2HP2ef.png',
      name: 'Sharepoint',
    },
    {
      id: 'onedrive',
      imageUrl: 'https://i.imgur.com/O4HZwjr.png',
      name: 'OneDrive',
    },
    {
      id: 'google-drive',
      imageUrl: 'https://i.imgur.com/VO1eicv.png',
      name: 'Google Drive',
    },
    {
      id: 'box',
      imageUrl: 'https://i.imgur.com/Ovl7PUi.png',
      name: 'Box',
    },
    {
      id: 'dropbox',
      imageUrl: 'https://i.imgur.com/QscZMxB.png',
      name: 'Dropbox',
    },
    {
      id: 'zapier',
      imageUrl: 'https://i.imgur.com/GceTk9p.png',
      name: 'Zapier',
    },
  ]
  const integrations = [
    {
      name: 'Salesforce',
      color: '#00A1E0',
      position: {
        x: '30%',
        y: '20%',
      },
      delay: 0.2,
    },
    {
      name: 'HubSpot',
      color: '#FF7A59',
      position: {
        x: '70%',
        y: '15%',
      },
      delay: 0.4,
    },
    {
      name: 'Slack',
      color: '#4A154B',
      position: {
        x: '85%',
        y: '35%',
      },
      delay: 0.6,
    },
    {
      name: 'Google Drive',
      color: '#0F9D58',
      position: {
        x: '75%',
        y: '65%',
      },
      delay: 0.8,
    },
    {
      name: 'Microsoft',
      color: '#00A4EF',
      position: {
        x: '25%',
        y: '70%',
      },
      delay: 1.0,
    },
    {
      name: 'Zapier',
      color: '#FF4A00',
      position: {
        x: '15%',
        y: '40%',
      },
      delay: 1.2,
    },
  ]
  const features = [
    {
      title: 'Simple Integration',
      description:
        'Just a few lines of code to request documents from your clients',
      icon: <CodeIcon className="h-6 w-6" />,
    },
    {
      title: 'Webhook Notifications',
      description:
        'Get real-time updates when documents are uploaded or processed',
      icon: <RefreshCwIcon className="h-6 w-6" />,
    },
    {
      title: 'Secure Storage',
      description:
        'All documents are encrypted and stored with enterprise-grade security',
      icon: <LockIcon className="h-6 w-6" />,
    },
  ]
  const flowSteps = [
    {
      step: 1,
      text: 'Send document request via API',
      icon: <CodeIcon className="h-4 w-4" />,
    },
    {
      step: 2,
      text: 'Client receives secure document upload link',
      icon: <LockIcon className="h-4 w-4" />,
    },
    {
      step: 3,
      text: 'Documents are uploaded and processed',
      icon: <RefreshCwIcon className="h-4 w-4" />,
    },
    {
      step: 4,
      text: 'Retrieve documents via API or dashboard',
      icon: <ZapIcon className="h-4 w-4" />,
    },
  ]
  return (
    <section
      ref={combinedRef}
      className="py-24 px-4 sm:px-6 lg:px-8 bg-black text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none z-0 overflow-hidden">
        {isMobile() ? (
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(125deg, #000000, #0a0f1a, #101e36, #0f1a33, #1a1a2e)',
              backgroundSize: '400% 400%',
              backgroundPosition: '50% 50%',
            }}
          />
        ) : (
          <div
            className="absolute inset-0 animate-[gradient_15s_ease_infinite]"
            style={{
              background:
                'linear-gradient(125deg, #000000, #0a0f1a, #101e36, #0f1a33, #1a1a2e)',
              backgroundSize: '400% 400%',
              willChange: 'background-position',
            }}
          />
        )}
        {!isMobile() && (
          <>
            <div
              className="absolute inset-0 opacity-30 animate-[gradient_18s_ease-in-out_infinite_alternate]"
              style={{
                background:
                  'radial-gradient(circle at 30% 50%, rgba(46, 107, 255, 0.2) 0%, rgba(0, 0, 0, 0) 50%)',
                backgroundSize: '200% 200%',
                willChange: 'background-position',
              }}
            />
            <div
              className="absolute inset-0 opacity-25 animate-[gradient_20s_ease-in-out_infinite_reverse]"
              style={{
                background:
                  'radial-gradient(circle at 70% 30%, rgba(0, 174, 255, 0.2) 0%, rgba(0, 0, 0, 0) 70%)',
                backgroundSize: '300% 300%',
                mixBlendMode: 'screen',
                willChange: 'background-position',
              }}
            />
          </>
        )}
        <style jsx>{`
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
            100% {
              transform: translateY(0px);
            }
          }
          .gradient-text {
            background: linear-gradient(
              90deg,
              #2e6bff 0%,
              #36d1dc 25%,
              #5b86e5 50%,
              #fc466b 75%,
              #2e6bff 100%
            );
            background-size: 200% auto;
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: gradient 4s linear infinite;
            display: inline-block;
            margin-left: 0.05em;
          }
        `}</style>
      </div>
      {!isMobile() && (
        <>
          <div className="absolute inset-x-0 top-0 h-[2px] bg-white/10"></div>
          <div className="absolute inset-x-0 bottom-0 h-[2px] bg-white/10"></div>
          <div className="absolute inset-y-0 left-0 w-[2px] bg-white/10"></div>
          <div className="absolute inset-y-0 right-0 w-[2px] bg-white/10"></div>
        </>
      )}
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: isMobile() ? 0.5 : 0.7,
            delay: isMobile() ? 0.1 : 0.2,
          }}
          className="mb-20"
        >
          <motion.div
            className="text-center mb-10"
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: isMobile() ? 0.5 : 0.7,
            }}
          >
            <h2 className="text-4xl sm:text-6xl font-bold mb-4 tracking-tight">
              Integrations
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Connect with your favorite tools and platforms
            </p>
          </motion.div>
          <div
            className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 ${isMobile() ? 'max-w-[95vw] mx-auto' : ''}`}
          >
            <motion.div
              className="flex items-center justify-center h-16 rounded-lg relative overflow-hidden group"
              whileHover={
                isMobile()
                  ? {}
                  : {
                      scale: 1.05,
                    }
              }
              transition={{
                duration: 0.2,
              }}
              style={{
                willChange: isMobile() ? 'auto' : 'transform',
              }}
            >
              <div
                className="absolute inset-0 bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] opacity-90"
                style={{
                  backgroundSize: '200% 200%',
                  animation: isMobile() ? 'none' : 'gradient 8s ease infinite',
                  backgroundPosition: isMobile() ? '0% 0%' : 'auto',
                }}
              ></div>
              <span className="relative z-10 text-white text-xs font-medium text-center px-2">
                Your Internal Systems
              </span>
            </motion.div>
            {integrationIcons.map((icon, index) => (
              <motion.div
                key={icon.id || index}
                className="flex items-center justify-center h-16 rounded-lg bg-white/90 border border-white/30 hover:border-white/50 shadow-sm transition-all"
                whileHover={
                  isMobile()
                    ? {}
                    : {
                        scale: 1.03,
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: {
                          duration: 0.05,
                          type: 'tween',
                          ease: 'easeOut',
                        },
                      }
                }
                style={{
                  willChange: isMobile()
                    ? 'auto'
                    : 'transform, opacity, background-color, box-shadow',
                }}
              >
                <img
                  src={icon.imageUrl || icon.logo}
                  alt={icon.name || icon.alt || 'Integration logo'}
                  className={`object-contain ${
                    icon.isExtraLarge
                      ? 'h-14 max-w-[100%]'
                      : icon.isLarger
                        ? 'h-12 max-w-[95%]'
                        : icon.id === 'box'
                          ? 'h-6 max-w-[51%]'
                          : icon.isSmaller
                            ? 'h-8 max-w-[75%]'
                            : 'h-10 max-w-[85%]'
                  } transition-transform duration-100`}
                  loading="lazy"
                />
              </motion.div>
            ))}
            <motion.div
              className="flex items-center justify-center h-16 rounded-lg relative overflow-hidden group"
              whileHover={
                isMobile()
                  ? {}
                  : {
                      scale: 1.05,
                    }
              }
              transition={{
                duration: 0.2,
              }}
            >
              <div
                className="absolute inset-0 bg-gradient-to-r from-[#5b86e5] to-[#fc466b] opacity-90"
                style={{
                  backgroundSize: '200% 200%',
                  animation: isMobile() ? 'none' : 'gradient 8s ease infinite',
                  backgroundPosition: isMobile() ? '0% 0%' : 'auto',
                }}
              ></div>
              <span className="relative z-10 text-white text-sm font-medium">
                and more...
              </span>
            </motion.div>
          </div>
        </motion.div>
        <motion.div
          className="text-center mb-20 relative"
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: isMobile() ? 0.5 : 0.7,
          }}
        >
          <h2 className="text-4xl sm:text-6xl font-bold mb-6 tracking-tight flex items-center justify-center">
            <img
              src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Ti5Jr9Zm1LRNz51qTKsea/1.png"
              alt="Pigeon Logo"
              className="h-[1em] mr-3"
            />
            <span className="text-white">Pigeon</span>
            <span
              className={`gradient-text italic relative px-[0.15em] ${isMobile() ? 'static' : ''}`}
            >
              API
            </span>
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            Create custom integrations with the help of Pigeon engineers
          </p>
        </motion.div>
        <div
          className={`grid md:grid-cols-2 gap-8 lg:gap-12 ${isMobile() ? 'grid-cols-1' : ''}`}
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: isMobile() ? 0.5 : 0.7,
            }}
            className="h-full flex flex-col"
          >
            <div className="bg-[#0F1729] border border-white/10 rounded-xl p-6 lg:p-8 h-full flex flex-col shadow-[0_0_40px_rgba(46,107,255,0.07)]">
              <div className="mb-6">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/5 text-white mb-4 backdrop-blur-sm border border-white/10">
                  <ZapIcon className="h-4 w-4 mr-2 text-[#2E6BFF]" />
                  <span className="text-sm font-medium tracking-wide">
                    POWERFUL & FLEXIBLE
                  </span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold tracking-tight">
                  Build with our API
                </h2>
              </div>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm rounded-lg p-5 border border-white/10"
                    initial={{
                      opacity: 0,
                      y: 10,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: 0.1 + index * (isMobile() ? 0.05 : 0.1),
                    }}
                    whileHover={
                      isMobile()
                        ? {}
                        : {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderColor: 'rgba(46, 107, 255, 0.5)',
                            y: -3,
                          }
                    }
                    style={{
                      willChange: isMobile()
                        ? 'auto'
                        : 'transform, background-color, border-color',
                    }}
                  >
                    <div className="flex items-start">
                      <div className="mr-4 p-3 bg-[#2E6BFF]/20 rounded-lg text-[#2E6BFF]">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-white/60 text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
          {(!isMobile() || inView) && (
            <motion.div
              className="h-full"
              style={{
                opacity,
              }}
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: isMobile() ? 0.5 : 0.7,
              }}
            >
              <motion.div
                className="bg-[#0B1220] rounded-xl p-4 shadow-[0_0_40px_rgba(46,107,255,0.15)] text-white font-mono text-sm overflow-hidden border border-white/10 h-full flex flex-col"
                whileHover={
                  isMobile()
                    ? {}
                    : {
                        boxShadow: '0 0 60px rgba(46,107,255,0.25)',
                      }
                }
                transition={{
                  duration: 0.3,
                }}
                style={{
                  willChange: isMobile() ? 'auto' : 'box-shadow',
                }}
              >
                {isMobile() ? (
                  <div className="flex-1 overflow-y-auto p-4 text-sm">
                    <pre className="text-white/80">
                      {`const pigeon = new PigeonAPI("your_api_key");
async function createDocumentRequest() {
  const response = await pigeon.documents.request({
    name: "Binding Documents",
    due_date: "2024-06-24T14:15:22Z",
    reminders: 3,
    recipients: [{
      first_name: "Jane",
      last_name: "Smith",
      email: "user@example.com"
    }],
    items: [...]
  });
  return response;
}`}
                    </pre>
                  </div>
                ) : (
                  <div className="relative flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                    <div className="pl-4 text-white/90 max-w-full">
                      <div className="relative">
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors">
                          <span className="text-purple-400">const</span>{' '}
                          <span className="text-blue-300">pigeon</span>{' '}
                          <span className="text-white">=</span>{' '}
                          <span className="text-purple-400">new</span>{' '}
                          <motion.span
                            className="text-[#2E6BFF]"
                            animate={{
                              color: ['#2E6BFF', '#36D1DC', '#2E6BFF'],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                            }}
                          >
                            PigeonAPI
                          </motion.span>
                          <span className="text-white">(</span>
                          <span className="text-yellow-300">
                            "your_api_key"
                          </span>
                          <span className="text-white">);</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors">
                          <span className="text-purple-400">async</span>{' '}
                          <span className="text-purple-400">function</span>{' '}
                          <span className="text-[#2E6BFF]">
                            createDocumentRequest
                          </span>
                          <span className="text-white">() {`{`}</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-4">
                          <span className="text-purple-400">const</span>{' '}
                          <span className="text-blue-300">response</span>{' '}
                          <span className="text-white">=</span>{' '}
                          <span className="text-purple-400">await</span>{' '}
                          <span className="text-[#2E6BFF]">
                            pigeon.documents.request
                          </span>
                          <span className="text-white">({`{`}</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-blue-300">name</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-yellow-300">
                            "Binding Documents"
                          </span>
                          <span className="text-white">,</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-blue-300">due_date</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-yellow-300">
                            "2024-06-24T14:15:22Z"
                          </span>
                          <span className="text-white">,</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-blue-300">reminders</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-purple-400">3</span>
                          <span className="text-white">,</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-blue-300">recipients</span>
                          <span className="text-white">: [{`{`}</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-12">
                          <span className="text-blue-300">first_name</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-yellow-300">"Jane"</span>
                          <span className="text-white">,</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-12">
                          <span className="text-blue-300">last_name</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-yellow-300">"Smith"</span>
                          <span className="text-white">,</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-12">
                          <span className="text-blue-300">email</span>
                          <span className="text-white">:</span>{' '}
                          <span className="text-yellow-300">
                            "user@example.com"
                          </span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-white">{`}]`},</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-8">
                          <span className="text-blue-300">items</span>
                          <span className="text-white">: [...]</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-4">
                          <span className="text-white">{`});`}</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors pl-4">
                          <span className="text-purple-400">return</span>
                          <span className="text-white"> response;</span>
                        </div>
                        <div className="py-1 px-2 rounded-md hover:bg-white/5 transition-colors">
                          <span className="text-white">{`}`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <motion.div
                  className="mt-2 flex items-center text-green-400 border-t border-white/10 pt-2"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  transition={{
                    delay: isMobile() ? 0.5 : 1,
                    duration: 0.5,
                  }}
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  <span>Document request sent successfully!</span>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </div>
        <motion.div
          className="flex flex-wrap justify-center gap-4 mt-10"
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: isMobile() ? 0.5 : 0.7,
            delay: isMobile() ? 0.2 : 0.3,
          }}
        >
          <motion.a
            href="https://docs.pigeondocuments.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 border border-white/20 text-base font-medium rounded-full text-white bg-white/5 hover:bg-white/10 transition-colors duration-300"
            whileHover={
              isMobile()
                ? {}
                : {
                    scale: 1.03,
                    borderColor: 'rgba(255,255,255,0.3)',
                  }
            }
            whileTap={{
              scale: 0.98,
            }}
            style={{
              willChange: isMobile() ? 'auto' : 'transform, border-color',
            }}
          >
            <CodeIcon className="h-5 w-5 mr-2" />
            API Docs
          </motion.a>
          <motion.a
            href="https://calendly.com/pigeondocuments"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-[#2E6BFF] hover:bg-[#1E5BFF] transition-colors duration-300"
            whileHover={
              isMobile()
                ? {}
                : {
                    scale: 1.03,
                    boxShadow: '0 0 20px rgba(46,107,255,0.5)',
                  }
            }
            whileTap={{
              scale: 0.98,
            }}
            style={{
              willChange: isMobile() ? 'auto' : 'transform, box-shadow',
            }}
          >
            Book a Call
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}
